// Profile data and generation utilities

// Function to generate consistent avatar based on username
export const getAvatarUrl = (username) => {
  // Create a simple hash from username to get consistent number
  let hash = 0;
  for (let i = 0; i < username.length; i++) {
    hash = ((hash << 5) - hash) + username.charCodeAt(i);
    hash = hash & hash; // Convert to 32bit integer
  }
  // Map to range 1-70 for pravatar images
  const imgId = Math.abs(hash) % 70 + 1;
  return `https://i.pravatar.cc/150?img=${imgId}&seed=${username}`;
};

// Seeded random number generator for consistent profiles.
// Use a 32-bit PRNG (Mulberry32) to avoid heavy collisions from small moduli.
export const seededRandom = (seed) => {
  let a = (seed >>> 0) || 0x6d2b79f5; // ensure non-zero uint32
  return () => {
    a = (a + 0x6D2B79F5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
};

// Create a 32-bit hash from location name for seeding (FNV-1a).
export const hashLocation = (locationName) => {
  let hash = 0x811c9dc5;
  for (let i = 0; i < locationName.length; i++) {
    hash ^= locationName.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
};

// All world locations
export const worldLocations = [
  // Berlin (10 locations)
  { lat: 52.5163, lng: 13.3777, name: 'Brandenburg Gate, Berlin', city: 'Berlin' },
  { lat: 52.5186, lng: 13.3763, name: 'Reichstag Building, Berlin', city: 'Berlin' },
  { lat: 52.5219, lng: 13.4006, name: 'Museum Island, Berlin', city: 'Berlin' },
  { lat: 52.5218, lng: 13.4133, name: 'Alexanderplatz, Berlin', city: 'Berlin' },
  { lat: 52.5190, lng: 13.4011, name: 'Berlin Cathedral', city: 'Berlin' },
  { lat: 52.5074, lng: 13.3904, name: 'Checkpoint Charlie, Berlin', city: 'Berlin' },
  { lat: 52.5213, lng: 13.3967, name: 'Pergamon Museum, Berlin', city: 'Berlin' },
  { lat: 52.5145, lng: 13.3501, name: 'Tiergarten, Berlin', city: 'Berlin' },
  { lat: 52.5054, lng: 13.4456, name: 'East Side Gallery, Berlin', city: 'Berlin' },
  { lat: 52.5136, lng: 13.3926, name: 'Gendarmenmarkt, Berlin', city: 'Berlin' },
  
  // London (5 locations)
  { lat: 51.5014, lng: -0.1419, name: 'Big Ben, London', city: 'London' },
  { lat: 51.4994, lng: -0.1245, name: 'London Eye', city: 'London' },
  { lat: 51.5074, lng: -0.1278, name: 'Tower Bridge, London', city: 'London' },
  { lat: 51.5099, lng: -0.1180, name: 'British Museum, London', city: 'London' },
  { lat: 51.5072, lng: -0.1276, name: 'Buckingham Palace, London', city: 'London' },
  
  // Paris (5 locations)
  { lat: 48.8584, lng: 2.2945, name: 'Eiffel Tower, Paris', city: 'Paris' },
  { lat: 48.8606, lng: 2.3376, name: 'Louvre Museum, Paris', city: 'Paris' },
  { lat: 48.8530, lng: 2.3499, name: 'Notre-Dame, Paris', city: 'Paris' },
  { lat: 48.8867, lng: 2.3431, name: 'Sacré-Cœur, Paris', city: 'Paris' },
  { lat: 48.8738, lng: 2.2950, name: 'Arc de Triomphe, Paris', city: 'Paris' },
  
  // New York (5 locations)
  { lat: 40.7589, lng: -73.9851, name: 'Times Square, New York', city: 'New York' },
  { lat: 40.6892, lng: -74.0445, name: 'Statue of Liberty, New York', city: 'New York' },
  { lat: 40.7580, lng: -73.9855, name: 'Central Park, New York', city: 'New York' },
  { lat: 40.7489, lng: -73.9680, name: 'Empire State Building, New York', city: 'New York' },
  { lat: 40.7505, lng: -73.9934, name: 'Brooklyn Bridge, New York', city: 'New York' },
  
  // Tokyo (5 locations)
  { lat: 35.6586, lng: 139.7454, name: 'Tokyo Tower', city: 'Tokyo' },
  { lat: 35.6762, lng: 139.6503, name: 'Shibuya Crossing, Tokyo', city: 'Tokyo' },
  { lat: 35.7148, lng: 139.7967, name: 'Senso-ji Temple, Tokyo', city: 'Tokyo' },
  { lat: 35.6852, lng: 139.7528, name: 'Imperial Palace, Tokyo', city: 'Tokyo' },
  { lat: 35.6586, lng: 139.7454, name: 'Shibuya, Tokyo', city: 'Tokyo' },
  
  // Rome (5 locations)
  { lat: 41.9028, lng: 12.4964, name: 'Colosseum, Rome', city: 'Rome' },
  { lat: 41.9022, lng: 12.4539, name: 'Vatican City, Rome', city: 'Rome' },
  { lat: 41.8986, lng: 12.4769, name: 'Trevi Fountain, Rome', city: 'Rome' },
  { lat: 41.8925, lng: 12.4853, name: 'Pantheon, Rome', city: 'Rome' },
  { lat: 41.8939, lng: 12.4834, name: 'Spanish Steps, Rome', city: 'Rome' },
  
  // Barcelona (3 locations)
  { lat: 41.4036, lng: 2.1744, name: 'Sagrada Família, Barcelona', city: 'Barcelona' },
  { lat: 41.3851, lng: 2.1734, name: 'Park Güell, Barcelona', city: 'Barcelona' },
  { lat: 41.3833, lng: 2.1769, name: 'La Rambla, Barcelona', city: 'Barcelona' },
  
  // Amsterdam (3 locations)
  { lat: 52.3676, lng: 4.9041, name: 'Anne Frank House, Amsterdam', city: 'Amsterdam' },
  { lat: 52.3702, lng: 4.8952, name: 'Rijksmuseum, Amsterdam', city: 'Amsterdam' },
  { lat: 52.3792, lng: 4.9003, name: 'Van Gogh Museum, Amsterdam', city: 'Amsterdam' },
  
  // Sydney (3 locations)
  { lat: -33.8568, lng: 151.2153, name: 'Sydney Opera House', city: 'Sydney' },
  { lat: -33.8523, lng: 151.2108, name: 'Sydney Harbour Bridge', city: 'Sydney' },
  { lat: -33.8688, lng: 151.2093, name: 'Bondi Beach, Sydney', city: 'Sydney' },
  
  // Dubai (3 locations)
  { lat: 25.2048, lng: 55.2708, name: 'Burj Khalifa, Dubai', city: 'Dubai' },
  { lat: 25.1972, lng: 55.2744, name: 'Dubai Mall', city: 'Dubai' },
  { lat: 25.0657, lng: 55.1398, name: 'Palm Jumeirah, Dubai', city: 'Dubai' },
  
  // Mumbai (3 locations)
  { lat: 18.9220, lng: 72.8347, name: 'Gateway of India, Mumbai', city: 'Mumbai' },
  { lat: 19.0760, lng: 72.8777, name: 'Marine Drive, Mumbai', city: 'Mumbai' },
  { lat: 18.9388, lng: 72.8354, name: 'Taj Mahal Palace, Mumbai', city: 'Mumbai' },
  
  // Istanbul (3 locations)
  { lat: 41.0086, lng: 28.9802, name: 'Hagia Sophia, Istanbul', city: 'Istanbul' },
  { lat: 41.0082, lng: 28.9784, name: 'Blue Mosque, Istanbul', city: 'Istanbul' },
  { lat: 41.0086, lng: 28.9750, name: 'Topkapi Palace, Istanbul', city: 'Istanbul' },
  
  // Cairo (2 locations)
  { lat: 29.9792, lng: 31.1342, name: 'Great Pyramid of Giza', city: 'Cairo' },
  { lat: 30.0444, lng: 31.2357, name: 'Egyptian Museum, Cairo', city: 'Cairo' },
  
  // Moscow (2 locations)
  { lat: 55.7558, lng: 37.6173, name: 'Red Square, Moscow', city: 'Moscow' },
  { lat: 55.7520, lng: 37.6175, name: 'Kremlin, Moscow', city: 'Moscow' },
  
  // Rio de Janeiro (2 locations)
  { lat: -22.9519, lng: -43.2105, name: 'Christ the Redeemer, Rio', city: 'Rio de Janeiro' },
  { lat: -22.9669, lng: -43.1808, name: 'Copacabana Beach, Rio', city: 'Rio de Janeiro' },
  
  // Los Angeles (5 locations)
  { lat: 34.0522, lng: -118.2437, name: 'Hollywood Sign, Los Angeles', city: 'Los Angeles' },
  { lat: 34.1015, lng: -118.3269, name: 'Universal Studios, Los Angeles', city: 'Los Angeles' },
  { lat: 34.0928, lng: -118.3287, name: 'Griffith Observatory, Los Angeles', city: 'Los Angeles' },
  { lat: 34.0522, lng: -118.2437, name: 'Santa Monica Pier, Los Angeles', city: 'Los Angeles' },
  { lat: 34.0736, lng: -118.4004, name: 'Beverly Hills, Los Angeles', city: 'Los Angeles' },
  
  // San Francisco (5 locations)
  { lat: 37.8199, lng: -122.4783, name: 'Golden Gate Bridge, San Francisco', city: 'San Francisco' },
  { lat: 37.8044, lng: -122.4180, name: 'Alcatraz Island, San Francisco', city: 'San Francisco' },
  { lat: 37.7749, lng: -122.4194, name: 'Fisherman\'s Wharf, San Francisco', city: 'San Francisco' },
  { lat: 37.7749, lng: -122.4194, name: 'Lombard Street, San Francisco', city: 'San Francisco' },
  { lat: 37.7849, lng: -122.4094, name: 'Chinatown, San Francisco', city: 'San Francisco' },
  
  // Chicago (5 locations)
  { lat: 41.8781, lng: -87.6298, name: 'Millennium Park, Chicago', city: 'Chicago' },
  { lat: 41.8789, lng: -87.6359, name: 'Willis Tower, Chicago', city: 'Chicago' },
  { lat: 41.8825, lng: -87.6221, name: 'Navy Pier, Chicago', city: 'Chicago' },
  { lat: 41.8781, lng: -87.6298, name: 'Art Institute of Chicago', city: 'Chicago' },
  { lat: 41.8789, lng: -87.6359, name: 'Magnificent Mile, Chicago', city: 'Chicago' },
  
  // Miami (4 locations)
  { lat: 25.7617, lng: -80.1918, name: 'South Beach, Miami', city: 'Miami' },
  { lat: 25.7907, lng: -80.1300, name: 'Wynwood Walls, Miami', city: 'Miami' },
  { lat: 25.7907, lng: -80.1300, name: 'Little Havana, Miami', city: 'Miami' },
  { lat: 25.7617, lng: -80.1918, name: 'Ocean Drive, Miami', city: 'Miami' },
  
  // Toronto (4 locations)
  { lat: 43.6532, lng: -79.3832, name: 'CN Tower, Toronto', city: 'Toronto' },
  { lat: 43.6532, lng: -79.3832, name: 'Royal Ontario Museum, Toronto', city: 'Toronto' },
  { lat: 43.6532, lng: -79.3832, name: 'St. Lawrence Market, Toronto', city: 'Toronto' },
  { lat: 43.6532, lng: -79.3832, name: 'Distillery District, Toronto', city: 'Toronto' },
  
  // Vancouver (4 locations)
  { lat: 49.2827, lng: -123.1207, name: 'Stanley Park, Vancouver', city: 'Vancouver' },
  { lat: 49.2827, lng: -123.1207, name: 'Granville Island, Vancouver', city: 'Vancouver' },
  { lat: 49.2827, lng: -123.1207, name: 'Capilano Suspension Bridge, Vancouver', city: 'Vancouver' },
  { lat: 49.2827, lng: -123.1207, name: 'Gastown, Vancouver', city: 'Vancouver' },
  
  // Madrid (5 locations)
  { lat: 40.4168, lng: -3.7038, name: 'Royal Palace, Madrid', city: 'Madrid' },
  { lat: 40.4168, lng: -3.7038, name: 'Prado Museum, Madrid', city: 'Madrid' },
  { lat: 40.4168, lng: -3.7038, name: 'Retiro Park, Madrid', city: 'Madrid' },
  { lat: 40.4168, lng: -3.7038, name: 'Plaza Mayor, Madrid', city: 'Madrid' },
  { lat: 40.4168, lng: -3.7038, name: 'Puerta del Sol, Madrid', city: 'Madrid' },
  
  // Vienna (4 locations)
  { lat: 48.2082, lng: 16.3738, name: 'Schönbrunn Palace, Vienna', city: 'Vienna' },
  { lat: 48.2082, lng: 16.3738, name: 'St. Stephen\'s Cathedral, Vienna', city: 'Vienna' },
  { lat: 48.2082, lng: 16.3738, name: 'Belvedere Palace, Vienna', city: 'Vienna' },
  { lat: 48.2082, lng: 16.3738, name: 'Hofburg Palace, Vienna', city: 'Vienna' },
  
  // Prague (4 locations)
  { lat: 50.0755, lng: 14.4378, name: 'Charles Bridge, Prague', city: 'Prague' },
  { lat: 50.0755, lng: 14.4378, name: 'Prague Castle, Prague', city: 'Prague' },
  { lat: 50.0755, lng: 14.4378, name: 'Old Town Square, Prague', city: 'Prague' },
  { lat: 50.0755, lng: 14.4378, name: 'Astronomical Clock, Prague', city: 'Prague' },
  
  // Budapest (4 locations)
  { lat: 47.4979, lng: 19.0402, name: 'Buda Castle, Budapest', city: 'Budapest' },
  { lat: 47.4979, lng: 19.0402, name: 'Parliament Building, Budapest', city: 'Budapest' },
  { lat: 47.4979, lng: 19.0402, name: 'Chain Bridge, Budapest', city: 'Budapest' },
  { lat: 47.4979, lng: 19.0402, name: 'Széchenyi Thermal Bath, Budapest', city: 'Budapest' },
  
  // Warsaw (3 locations)
  { lat: 52.2297, lng: 21.0122, name: 'Old Town, Warsaw', city: 'Warsaw' },
  { lat: 52.2297, lng: 21.0122, name: 'Royal Castle, Warsaw', city: 'Warsaw' },
  { lat: 52.2297, lng: 21.0122, name: 'Palace of Culture, Warsaw', city: 'Warsaw' },
  
  // Lisbon (4 locations)
  { lat: 38.7223, lng: -9.1393, name: 'Belém Tower, Lisbon', city: 'Lisbon' },
  { lat: 38.7223, lng: -9.1393, name: 'Jerónimos Monastery, Lisbon', city: 'Lisbon' },
  { lat: 38.7223, lng: -9.1393, name: 'Alfama District, Lisbon', city: 'Lisbon' },
  { lat: 38.7223, lng: -9.1393, name: 'São Jorge Castle, Lisbon', city: 'Lisbon' },
  
  // Athens (4 locations)
  { lat: 37.9838, lng: 23.7275, name: 'Acropolis, Athens', city: 'Athens' },
  { lat: 37.9838, lng: 23.7275, name: 'Parthenon, Athens', city: 'Athens' },
  { lat: 37.9838, lng: 23.7275, name: 'Plaka, Athens', city: 'Athens' },
  { lat: 37.9838, lng: 23.7275, name: 'Temple of Olympian Zeus, Athens', city: 'Athens' },
  
  // Singapore (5 locations)
  { lat: 1.3521, lng: 103.8198, name: 'Marina Bay Sands, Singapore', city: 'Singapore' },
  { lat: 1.3521, lng: 103.8198, name: 'Gardens by the Bay, Singapore', city: 'Singapore' },
  { lat: 1.3521, lng: 103.8198, name: 'Sentosa Island, Singapore', city: 'Singapore' },
  { lat: 1.3521, lng: 103.8198, name: 'Orchard Road, Singapore', city: 'Singapore' },
  { lat: 1.3521, lng: 103.8198, name: 'Merlion Park, Singapore', city: 'Singapore' },
  
  // Hong Kong (5 locations)
  { lat: 22.3193, lng: 114.1694, name: 'Victoria Peak, Hong Kong', city: 'Hong Kong' },
  { lat: 22.3193, lng: 114.1694, name: 'Tsim Sha Tsui, Hong Kong', city: 'Hong Kong' },
  { lat: 22.3193, lng: 114.1694, name: 'Temple Street Night Market, Hong Kong', city: 'Hong Kong' },
  { lat: 22.3193, lng: 114.1694, name: 'Big Buddha, Hong Kong', city: 'Hong Kong' },
  { lat: 22.3193, lng: 114.1694, name: 'Star Ferry, Hong Kong', city: 'Hong Kong' },
  
  // Seoul (5 locations)
  { lat: 37.5665, lng: 126.9780, name: 'Gyeongbokgung Palace, Seoul', city: 'Seoul' },
  { lat: 37.5665, lng: 126.9780, name: 'Myeongdong, Seoul', city: 'Seoul' },
  { lat: 37.5665, lng: 126.9780, name: 'N Seoul Tower, Seoul', city: 'Seoul' },
  { lat: 37.5665, lng: 126.9780, name: 'Bukchon Hanok Village, Seoul', city: 'Seoul' },
  { lat: 37.5665, lng: 126.9780, name: 'Gangnam, Seoul', city: 'Seoul' },
  
  // Beijing (5 locations)
  { lat: 39.9042, lng: 116.4074, name: 'Forbidden City, Beijing', city: 'Beijing' },
  { lat: 39.9042, lng: 116.4074, name: 'Great Wall of China, Beijing', city: 'Beijing' },
  { lat: 39.9042, lng: 116.4074, name: 'Temple of Heaven, Beijing', city: 'Beijing' },
  { lat: 39.9042, lng: 116.4074, name: 'Summer Palace, Beijing', city: 'Beijing' },
  { lat: 39.9042, lng: 116.4074, name: 'Tiananmen Square, Beijing', city: 'Beijing' },
  
  // Shanghai (4 locations)
  { lat: 31.2304, lng: 121.4737, name: 'The Bund, Shanghai', city: 'Shanghai' },
  { lat: 31.2304, lng: 121.4737, name: 'Yu Garden, Shanghai', city: 'Shanghai' },
  { lat: 31.2304, lng: 121.4737, name: 'Oriental Pearl Tower, Shanghai', city: 'Shanghai' },
  { lat: 31.2304, lng: 121.4737, name: 'Nanjing Road, Shanghai', city: 'Shanghai' },
  
  // Bangkok (5 locations)
  { lat: 13.7563, lng: 100.5018, name: 'Grand Palace, Bangkok', city: 'Bangkok' },
  { lat: 13.7563, lng: 100.5018, name: 'Wat Pho, Bangkok', city: 'Bangkok' },
  { lat: 13.7563, lng: 100.5018, name: 'Chatuchak Weekend Market, Bangkok', city: 'Bangkok' },
  { lat: 13.7563, lng: 100.5018, name: 'Wat Arun, Bangkok', city: 'Bangkok' },
  { lat: 13.7563, lng: 100.5018, name: 'Khao San Road, Bangkok', city: 'Bangkok' },
  
  // Kuala Lumpur (4 locations)
  { lat: 3.1390, lng: 101.6869, name: 'Petronas Towers, Kuala Lumpur', city: 'Kuala Lumpur' },
  { lat: 3.1390, lng: 101.6869, name: 'Batu Caves, Kuala Lumpur', city: 'Kuala Lumpur' },
  { lat: 3.1390, lng: 101.6869, name: 'Merdeka Square, Kuala Lumpur', city: 'Kuala Lumpur' },
  { lat: 3.1390, lng: 101.6869, name: 'KL Tower, Kuala Lumpur', city: 'Kuala Lumpur' },
  
  // Jakarta (4 locations)
  { lat: -6.2088, lng: 106.8456, name: 'National Monument, Jakarta', city: 'Jakarta' },
  { lat: -6.2088, lng: 106.8456, name: 'Istiqlal Mosque, Jakarta', city: 'Jakarta' },
  { lat: -6.2088, lng: 106.8456, name: 'Old Town, Jakarta', city: 'Jakarta' },
  { lat: -6.2088, lng: 106.8456, name: 'Taman Mini Indonesia, Jakarta', city: 'Jakarta' },
  
  // Melbourne (4 locations)
  { lat: -37.8136, lng: 144.9631, name: 'Federation Square, Melbourne', city: 'Melbourne' },
  { lat: -37.8136, lng: 144.9631, name: 'Royal Botanic Gardens, Melbourne', city: 'Melbourne' },
  { lat: -37.8136, lng: 144.9631, name: 'St. Kilda Beach, Melbourne', city: 'Melbourne' },
  { lat: -37.8136, lng: 144.9631, name: 'Great Ocean Road, Melbourne', city: 'Melbourne' },
  
  // Auckland (3 locations)
  { lat: -36.8485, lng: 174.7633, name: 'Sky Tower, Auckland', city: 'Auckland' },
  { lat: -36.8485, lng: 174.7633, name: 'Auckland Harbour Bridge, Auckland', city: 'Auckland' },
  { lat: -36.8485, lng: 174.7633, name: 'Waiheke Island, Auckland', city: 'Auckland' },
  
  // Buenos Aires (4 locations)
  { lat: -34.6037, lng: -58.3816, name: 'La Boca, Buenos Aires', city: 'Buenos Aires' },
  { lat: -34.6037, lng: -58.3816, name: 'Recoleta Cemetery, Buenos Aires', city: 'Buenos Aires' },
  { lat: -34.6037, lng: -58.3816, name: 'Plaza de Mayo, Buenos Aires', city: 'Buenos Aires' },
  { lat: -34.6037, lng: -58.3816, name: 'Teatro Colón, Buenos Aires', city: 'Buenos Aires' },
  
  // São Paulo (4 locations)
  { lat: -23.5505, lng: -46.6333, name: 'Paulista Avenue, São Paulo', city: 'São Paulo' },
  { lat: -23.5505, lng: -46.6333, name: 'Ibirapuera Park, São Paulo', city: 'São Paulo' },
  { lat: -23.5505, lng: -46.6333, name: 'Mercado Municipal, São Paulo', city: 'São Paulo' },
  { lat: -23.5505, lng: -46.6333, name: 'Liberdade, São Paulo', city: 'São Paulo' },
  
  // Mexico City (4 locations)
  { lat: 19.4326, lng: -99.1332, name: 'Zócalo, Mexico City', city: 'Mexico City' },
  { lat: 19.4326, lng: -99.1332, name: 'Frida Kahlo Museum, Mexico City', city: 'Mexico City' },
  { lat: 19.4326, lng: -99.1332, name: 'Chapultepec Park, Mexico City', city: 'Mexico City' },
  { lat: 19.4326, lng: -99.1332, name: 'Teotihuacan, Mexico City', city: 'Mexico City' },
  
  // Lima (3 locations)
  { lat: -12.0464, lng: -77.0428, name: 'Plaza Mayor, Lima', city: 'Lima' },
  { lat: -12.0464, lng: -77.0428, name: 'Miraflores, Lima', city: 'Lima' },
  { lat: -12.0464, lng: -77.0428, name: 'Barranco, Lima', city: 'Lima' },
  
  // Johannesburg (3 locations)
  { lat: -26.2041, lng: 28.0473, name: 'Apartheid Museum, Johannesburg', city: 'Johannesburg' },
  { lat: -26.2041, lng: 28.0473, name: 'Soweto, Johannesburg', city: 'Johannesburg' },
  { lat: -26.2041, lng: 28.0473, name: 'Gold Reef City, Johannesburg', city: 'Johannesburg' },
  
  // Cape Town (4 locations)
  { lat: -33.9249, lng: 18.4241, name: 'Table Mountain, Cape Town', city: 'Cape Town' },
  { lat: -33.9249, lng: 18.4241, name: 'Robben Island, Cape Town', city: 'Cape Town' },
  { lat: -33.9249, lng: 18.4241, name: 'V&A Waterfront, Cape Town', city: 'Cape Town' },
  { lat: -33.9249, lng: 18.4241, name: 'Boulders Beach, Cape Town', city: 'Cape Town' },
  
  // Casablanca (3 locations)
  { lat: 33.5731, lng: -7.5898, name: 'Hassan II Mosque, Casablanca', city: 'Casablanca' },
  { lat: 33.5731, lng: -7.5898, name: 'Old Medina, Casablanca', city: 'Casablanca' },
  { lat: 33.5731, lng: -7.5898, name: 'Corniche, Casablanca', city: 'Casablanca' },
  
  // Marrakech (3 locations)
  { lat: 31.6295, lng: -7.9811, name: 'Jemaa el-Fnaa, Marrakech', city: 'Marrakech' },
  { lat: 31.6295, lng: -7.9811, name: 'Bahia Palace, Marrakech', city: 'Marrakech' },
  { lat: 31.6295, lng: -7.9811, name: 'Majorelle Garden, Marrakech', city: 'Marrakech' },
  
  // Tel Aviv (3 locations)
  { lat: 32.0853, lng: 34.7818, name: 'Old Jaffa, Tel Aviv', city: 'Tel Aviv' },
  { lat: 32.0853, lng: 34.7818, name: 'Carmel Market, Tel Aviv', city: 'Tel Aviv' },
  { lat: 32.0853, lng: 34.7818, name: 'Tel Aviv Beach, Tel Aviv', city: 'Tel Aviv' },
  
  // Jerusalem (3 locations)
  { lat: 31.7683, lng: 35.2137, name: 'Western Wall, Jerusalem', city: 'Jerusalem' },
  { lat: 31.7683, lng: 35.2137, name: 'Church of the Holy Sepulchre, Jerusalem', city: 'Jerusalem' },
  { lat: 31.7683, lng: 35.2137, name: 'Dome of the Rock, Jerusalem', city: 'Jerusalem' },
  
  // Mumbai (2 more locations)
  { lat: 19.0760, lng: 72.8777, name: 'Bandra-Worli Sea Link, Mumbai', city: 'Mumbai' },
  { lat: 18.9388, lng: 72.8354, name: 'Colaba Causeway, Mumbai', city: 'Mumbai' },
  
  // Delhi (4 locations)
  { lat: 28.6139, lng: 77.2090, name: 'Red Fort, Delhi', city: 'Delhi' },
  { lat: 28.6139, lng: 77.2090, name: 'India Gate, Delhi', city: 'Delhi' },
  { lat: 28.6139, lng: 77.2090, name: 'Lotus Temple, Delhi', city: 'Delhi' },
  { lat: 28.6139, lng: 77.2090, name: 'Qutub Minar, Delhi', city: 'Delhi' },
  
  // Bangalore (3 locations)
  { lat: 12.9716, lng: 77.5946, name: 'Lalbagh Botanical Garden, Bangalore', city: 'Bangalore' },
  { lat: 12.9716, lng: 77.5946, name: 'Cubbon Park, Bangalore', city: 'Bangalore' },
  { lat: 12.9716, lng: 77.5946, name: 'Bangalore Palace, Bangalore', city: 'Bangalore' },
  
  // Additional 200 locations across the world
  // Montreal (4 locations)
  { lat: 45.5017, lng: -73.5673, name: 'Old Montreal, Montreal', city: 'Montreal' },
  { lat: 45.5088, lng: -73.5878, name: 'Mount Royal, Montreal', city: 'Montreal' },
  { lat: 45.5017, lng: -73.5673, name: 'Notre-Dame Basilica, Montreal', city: 'Montreal' },
  { lat: 45.5088, lng: -73.5878, name: 'Montreal Botanical Garden, Montreal', city: 'Montreal' },
  
  // Boston (4 locations)
  { lat: 42.3601, lng: -71.0589, name: 'Faneuil Hall, Boston', city: 'Boston' },
  { lat: 42.3551, lng: -71.0656, name: 'Boston Common, Boston', city: 'Boston' },
  { lat: 42.3601, lng: -71.0589, name: 'Fenway Park, Boston', city: 'Boston' },
  { lat: 42.3601, lng: -71.0589, name: 'Harvard University, Boston', city: 'Boston' },
  
  // Seattle (4 locations)
  { lat: 47.6062, lng: -122.3321, name: 'Space Needle, Seattle', city: 'Seattle' },
  { lat: 47.6062, lng: -122.3321, name: 'Pike Place Market, Seattle', city: 'Seattle' },
  { lat: 47.6062, lng: -122.3321, name: 'Seattle Center, Seattle', city: 'Seattle' },
  { lat: 47.6062, lng: -122.3321, name: 'Chihuly Garden, Seattle', city: 'Seattle' },
  
  // Las Vegas (4 locations)
  { lat: 36.1699, lng: -115.1398, name: 'Las Vegas Strip, Las Vegas', city: 'Las Vegas' },
  { lat: 36.1699, lng: -115.1398, name: 'Bellagio Fountains, Las Vegas', city: 'Las Vegas' },
  { lat: 36.1699, lng: -115.1398, name: 'Caesars Palace, Las Vegas', city: 'Las Vegas' },
  { lat: 36.1699, lng: -115.1398, name: 'Fremont Street, Las Vegas', city: 'Las Vegas' },
  
  // Denver (3 locations)
  { lat: 39.7392, lng: -104.9903, name: 'Red Rocks Amphitheatre, Denver', city: 'Denver' },
  { lat: 39.7392, lng: -104.9903, name: 'Denver Art Museum, Denver', city: 'Denver' },
  { lat: 39.7392, lng: -104.9903, name: 'Larimer Square, Denver', city: 'Denver' },
  
  // Portland (3 locations)
  { lat: 45.5152, lng: -122.6784, name: 'Powell\'s City of Books, Portland', city: 'Portland' },
  { lat: 45.5152, lng: -122.6784, name: 'Washington Park, Portland', city: 'Portland' },
  { lat: 45.5152, lng: -122.6784, name: 'Portland Japanese Garden, Portland', city: 'Portland' },
  
  // Edinburgh (4 locations)
  { lat: 55.9533, lng: -3.1883, name: 'Edinburgh Castle, Edinburgh', city: 'Edinburgh' },
  { lat: 55.9533, lng: -3.1883, name: 'Royal Mile, Edinburgh', city: 'Edinburgh' },
  { lat: 55.9533, lng: -3.1883, name: 'Arthur\'s Seat, Edinburgh', city: 'Edinburgh' },
  { lat: 55.9533, lng: -3.1883, name: 'Holyrood Palace, Edinburgh', city: 'Edinburgh' },
  
  // Dublin (4 locations)
  { lat: 53.3498, lng: -6.2603, name: 'Trinity College, Dublin', city: 'Dublin' },
  { lat: 53.3498, lng: -6.2603, name: 'Temple Bar, Dublin', city: 'Dublin' },
  { lat: 53.3498, lng: -6.2603, name: 'Guinness Storehouse, Dublin', city: 'Dublin' },
  { lat: 53.3498, lng: -6.2603, name: 'Dublin Castle, Dublin', city: 'Dublin' },
  
  // Brussels (3 locations)
  { lat: 50.8503, lng: 4.3517, name: 'Grand Place, Brussels', city: 'Brussels' },
  { lat: 50.8503, lng: 4.3517, name: 'Atomium, Brussels', city: 'Brussels' },
  { lat: 50.8503, lng: 4.3517, name: 'Manneken Pis, Brussels', city: 'Brussels' },
  
  // Stockholm (4 locations)
  { lat: 59.3293, lng: 18.0686, name: 'Gamla Stan, Stockholm', city: 'Stockholm' },
  { lat: 59.3293, lng: 18.0686, name: 'Vasa Museum, Stockholm', city: 'Stockholm' },
  { lat: 59.3293, lng: 18.0686, name: 'Royal Palace, Stockholm', city: 'Stockholm' },
  { lat: 59.3293, lng: 18.0686, name: 'ABBA Museum, Stockholm', city: 'Stockholm' },
  
  // Copenhagen (4 locations)
  { lat: 55.6761, lng: 12.5683, name: 'Nyhavn, Copenhagen', city: 'Copenhagen' },
  { lat: 55.6761, lng: 12.5683, name: 'Tivoli Gardens, Copenhagen', city: 'Copenhagen' },
  { lat: 55.6761, lng: 12.5683, name: 'Little Mermaid, Copenhagen', city: 'Copenhagen' },
  { lat: 55.6761, lng: 12.5683, name: 'Amalienborg Palace, Copenhagen', city: 'Copenhagen' },
  
  // Oslo (3 locations)
  { lat: 59.9139, lng: 10.7522, name: 'Vigeland Park, Oslo', city: 'Oslo' },
  { lat: 59.9139, lng: 10.7522, name: 'Royal Palace, Oslo', city: 'Oslo' },
  { lat: 59.9139, lng: 10.7522, name: 'Viking Ship Museum, Oslo', city: 'Oslo' },
  
  // Helsinki (3 locations)
  { lat: 60.1699, lng: 24.9384, name: 'Helsinki Cathedral, Helsinki', city: 'Helsinki' },
  { lat: 60.1699, lng: 24.9384, name: 'Suomenlinna, Helsinki', city: 'Helsinki' },
  { lat: 60.1699, lng: 24.9384, name: 'Temppeliaukio Church, Helsinki', city: 'Helsinki' },
  
  // Zurich (3 locations)
  { lat: 47.3769, lng: 8.5417, name: 'Old Town, Zurich', city: 'Zurich' },
  { lat: 47.3769, lng: 8.5417, name: 'Lake Zurich, Zurich', city: 'Zurich' },
  { lat: 47.3769, lng: 8.5417, name: 'Swiss National Museum, Zurich', city: 'Zurich' },
  
  // Geneva (3 locations)
  { lat: 46.2044, lng: 6.1432, name: 'Jet d\'Eau, Geneva', city: 'Geneva' },
  { lat: 46.2044, lng: 6.1432, name: 'United Nations, Geneva', city: 'Geneva' },
  { lat: 46.2044, lng: 6.1432, name: 'St. Pierre Cathedral, Geneva', city: 'Geneva' },
  
  // Munich (4 locations)
  { lat: 48.1351, lng: 11.5820, name: 'Marienplatz, Munich', city: 'Munich' },
  { lat: 48.1351, lng: 11.5820, name: 'Neuschwanstein Castle, Munich', city: 'Munich' },
  { lat: 48.1351, lng: 11.5820, name: 'English Garden, Munich', city: 'Munich' },
  { lat: 48.1351, lng: 11.5820, name: 'Hofbräuhaus, Munich', city: 'Munich' },
  
  // Frankfurt (3 locations)
  { lat: 50.1109, lng: 8.6821, name: 'Römer, Frankfurt', city: 'Frankfurt' },
  { lat: 50.1109, lng: 8.6821, name: 'Old Opera House, Frankfurt', city: 'Frankfurt' },
  { lat: 50.1109, lng: 8.6821, name: 'Städel Museum, Frankfurt', city: 'Frankfurt' },
  
  // Hamburg (3 locations)
  { lat: 53.5511, lng: 9.9937, name: 'Elbphilharmonie, Hamburg', city: 'Hamburg' },
  { lat: 53.5511, lng: 9.9937, name: 'Speicherstadt, Hamburg', city: 'Hamburg' },
  { lat: 53.5511, lng: 9.9937, name: 'St. Michael\'s Church, Hamburg', city: 'Hamburg' },
  
  // Cologne (3 locations)
  { lat: 50.9375, lng: 6.9603, name: 'Cologne Cathedral, Cologne', city: 'Cologne' },
  { lat: 50.9375, lng: 6.9603, name: 'Old Town, Cologne', city: 'Cologne' },
  { lat: 50.9375, lng: 6.9603, name: 'Hohenzollern Bridge, Cologne', city: 'Cologne' },
  
  // Lyon (3 locations)
  { lat: 45.7640, lng: 4.8357, name: 'Basilica of Notre-Dame, Lyon', city: 'Lyon' },
  { lat: 45.7640, lng: 4.8357, name: 'Old Lyon, Lyon', city: 'Lyon' },
  { lat: 45.7640, lng: 4.8357, name: 'Place Bellecour, Lyon', city: 'Lyon' },
  
  // Marseille (3 locations)
  { lat: 43.2965, lng: 5.3698, name: 'Old Port, Marseille', city: 'Marseille' },
  { lat: 43.2965, lng: 5.3698, name: 'Notre-Dame de la Garde, Marseille', city: 'Marseille' },
  { lat: 43.2965, lng: 5.3698, name: 'Calanques, Marseille', city: 'Marseille' },
  
  // Nice (3 locations)
  { lat: 43.7102, lng: 7.2620, name: 'Promenade des Anglais, Nice', city: 'Nice' },
  { lat: 43.7102, lng: 7.2620, name: 'Old Town, Nice', city: 'Nice' },
  { lat: 43.7102, lng: 7.2620, name: 'Castle Hill, Nice', city: 'Nice' },
  
  // Florence (4 locations)
  { lat: 43.7696, lng: 11.2558, name: 'Duomo, Florence', city: 'Florence' },
  { lat: 43.7696, lng: 11.2558, name: 'Uffizi Gallery, Florence', city: 'Florence' },
  { lat: 43.7696, lng: 11.2558, name: 'Ponte Vecchio, Florence', city: 'Florence' },
  { lat: 43.7696, lng: 11.2558, name: 'Piazza della Signoria, Florence', city: 'Florence' },
  
  // Venice (4 locations)
  { lat: 45.4408, lng: 12.3155, name: 'St. Mark\'s Square, Venice', city: 'Venice' },
  { lat: 45.4408, lng: 12.3155, name: 'Grand Canal, Venice', city: 'Venice' },
  { lat: 45.4408, lng: 12.3155, name: 'Rialto Bridge, Venice', city: 'Venice' },
  { lat: 45.4408, lng: 12.3155, name: 'Doge\'s Palace, Venice', city: 'Venice' },
  
  // Milan (4 locations)
  { lat: 45.4642, lng: 9.1900, name: 'Duomo di Milano, Milan', city: 'Milan' },
  { lat: 45.4642, lng: 9.1900, name: 'La Scala, Milan', city: 'Milan' },
  { lat: 45.4642, lng: 9.1900, name: 'Galleria Vittorio Emanuele, Milan', city: 'Milan' },
  { lat: 45.4642, lng: 9.1900, name: 'Sforza Castle, Milan', city: 'Milan' },
  
  // Naples (3 locations)
  { lat: 40.8518, lng: 14.2681, name: 'Pompeii, Naples', city: 'Naples' },
  { lat: 40.8518, lng: 14.2681, name: 'Mount Vesuvius, Naples', city: 'Naples' },
  { lat: 40.8518, lng: 14.2681, name: 'Old Town, Naples', city: 'Naples' },
  
  // Valencia (3 locations)
  { lat: 39.4699, lng: -0.3763, name: 'City of Arts and Sciences, Valencia', city: 'Valencia' },
  { lat: 39.4699, lng: -0.3763, name: 'Valencia Cathedral, Valencia', city: 'Valencia' },
  { lat: 39.4699, lng: -0.3763, name: 'Central Market, Valencia', city: 'Valencia' },
  
  // Seville (3 locations)
  { lat: 37.3891, lng: -5.9845, name: 'Alcázar, Seville', city: 'Seville' },
  { lat: 37.3891, lng: -5.9845, name: 'Seville Cathedral, Seville', city: 'Seville' },
  { lat: 37.3891, lng: -5.9845, name: 'Plaza de España, Seville', city: 'Seville' },
  
  // Granada (3 locations)
  { lat: 37.1773, lng: -3.5986, name: 'Alhambra, Granada', city: 'Granada' },
  { lat: 37.1773, lng: -3.5986, name: 'Generalife, Granada', city: 'Granada' },
  { lat: 37.1773, lng: -3.5986, name: 'Albaicín, Granada', city: 'Granada' },
  
  // Porto (3 locations)
  { lat: 41.1579, lng: -8.6291, name: 'Ribeira, Porto', city: 'Porto' },
  { lat: 41.1579, lng: -8.6291, name: 'Dom Luís Bridge, Porto', city: 'Porto' },
  { lat: 41.1579, lng: -8.6291, name: 'Livraria Lello, Porto', city: 'Porto' },
  
  // Krakow (4 locations)
  { lat: 50.0647, lng: 19.9450, name: 'Main Market Square, Krakow', city: 'Krakow' },
  { lat: 50.0647, lng: 19.9450, name: 'Wawel Castle, Krakow', city: 'Krakow' },
  { lat: 50.0647, lng: 19.9450, name: 'Auschwitz-Birkenau, Krakow', city: 'Krakow' },
  { lat: 50.0647, lng: 19.9450, name: 'Old Town, Krakow', city: 'Krakow' },
  
  // Bucharest (3 locations)
  { lat: 44.4268, lng: 26.1025, name: 'Palace of Parliament, Bucharest', city: 'Bucharest' },
  { lat: 44.4268, lng: 26.1025, name: 'Old Town, Bucharest', city: 'Bucharest' },
  { lat: 44.4268, lng: 26.1025, name: 'Herăstrău Park, Bucharest', city: 'Bucharest' },
  
  // Sofia (3 locations)
  { lat: 42.6977, lng: 23.3219, name: 'Alexander Nevsky Cathedral, Sofia', city: 'Sofia' },
  { lat: 42.6977, lng: 23.3219, name: 'Boyana Church, Sofia', city: 'Sofia' },
  { lat: 42.6977, lng: 23.3219, name: 'Vitosha Boulevard, Sofia', city: 'Sofia' },
  
  // Zagreb (3 locations)
  { lat: 45.8150, lng: 15.9819, name: 'Upper Town, Zagreb', city: 'Zagreb' },
  { lat: 45.8150, lng: 15.9819, name: 'Cathedral, Zagreb', city: 'Zagreb' },
  { lat: 45.8150, lng: 15.9819, name: 'Museum of Broken Relationships, Zagreb', city: 'Zagreb' },
  
  // Belgrade (3 locations)
  { lat: 44.7866, lng: 20.4489, name: 'Belgrade Fortress, Belgrade', city: 'Belgrade' },
  { lat: 44.7866, lng: 20.4489, name: 'St. Sava Temple, Belgrade', city: 'Belgrade' },
  { lat: 44.7866, lng: 20.4489, name: 'Skadarlija, Belgrade', city: 'Belgrade' },
  
  // Kyiv (4 locations)
  { lat: 50.4501, lng: 30.5234, name: 'Independence Square, Kyiv', city: 'Kyiv' },
  { lat: 50.4501, lng: 30.5234, name: 'St. Sophia\'s Cathedral, Kyiv', city: 'Kyiv' },
  { lat: 50.4501, lng: 30.5234, name: 'Pechersk Lavra, Kyiv', city: 'Kyiv' },
  { lat: 50.4501, lng: 30.5234, name: 'Golden Gate, Kyiv', city: 'Kyiv' },
  
  // Warsaw (2 more locations)
  { lat: 52.2297, lng: 21.0122, name: 'Warsaw Uprising Museum, Warsaw', city: 'Warsaw' },
  { lat: 52.2297, lng: 21.0122, name: 'Łazienki Park, Warsaw', city: 'Warsaw' },
  
  // Osaka (4 locations)
  { lat: 34.6937, lng: 135.5023, name: 'Osaka Castle, Osaka', city: 'Osaka' },
  { lat: 34.6937, lng: 135.5023, name: 'Dotonbori, Osaka', city: 'Osaka' },
  { lat: 34.6937, lng: 135.5023, name: 'Universal Studios Japan, Osaka', city: 'Osaka' },
  { lat: 34.6937, lng: 135.5023, name: 'Shinsekai, Osaka', city: 'Osaka' },
  
  // Kyoto (5 locations)
  { lat: 35.0116, lng: 135.7681, name: 'Fushimi Inari Shrine, Kyoto', city: 'Kyoto' },
  { lat: 35.0116, lng: 135.7681, name: 'Kinkaku-ji, Kyoto', city: 'Kyoto' },
  { lat: 35.0116, lng: 135.7681, name: 'Arashiyama, Kyoto', city: 'Kyoto' },
  { lat: 35.0116, lng: 135.7681, name: 'Gion District, Kyoto', city: 'Kyoto' },
  { lat: 35.0116, lng: 135.7681, name: 'Kiyomizu-dera, Kyoto', city: 'Kyoto' },
  
  // Yokohama (3 locations)
  { lat: 35.4437, lng: 139.6380, name: 'Minato Mirai, Yokohama', city: 'Yokohama' },
  { lat: 35.4437, lng: 139.6380, name: 'Chinatown, Yokohama', city: 'Yokohama' },
  { lat: 35.4437, lng: 139.6380, name: 'Sankeien Garden, Yokohama', city: 'Yokohama' },
  
  // Osaka (1 more location)
  { lat: 34.6937, lng: 135.5023, name: 'Tennoji Park, Osaka', city: 'Osaka' },
  
  // Taipei (4 locations)
  { lat: 25.0330, lng: 121.5654, name: 'Taipei 101, Taipei', city: 'Taipei' },
  { lat: 25.0330, lng: 121.5654, name: 'National Palace Museum, Taipei', city: 'Taipei' },
  { lat: 25.0330, lng: 121.5654, name: 'Shilin Night Market, Taipei', city: 'Taipei' },
  { lat: 25.0330, lng: 121.5654, name: 'Longshan Temple, Taipei', city: 'Taipei' },
  
  // Manila (3 locations)
  { lat: 14.5995, lng: 120.9842, name: 'Intramuros, Manila', city: 'Manila' },
  { lat: 14.5995, lng: 120.9842, name: 'Rizal Park, Manila', city: 'Manila' },
  { lat: 14.5995, lng: 120.9842, name: 'SM Mall of Asia, Manila', city: 'Manila' },
  
  // Ho Chi Minh City (4 locations)
  { lat: 10.8231, lng: 106.6297, name: 'Notre-Dame Cathedral, Ho Chi Minh City', city: 'Ho Chi Minh City' },
  { lat: 10.8231, lng: 106.6297, name: 'Ben Thanh Market, Ho Chi Minh City', city: 'Ho Chi Minh City' },
  { lat: 10.8231, lng: 106.6297, name: 'War Remnants Museum, Ho Chi Minh City', city: 'Ho Chi Minh City' },
  { lat: 10.8231, lng: 106.6297, name: 'Cu Chi Tunnels, Ho Chi Minh City', city: 'Ho Chi Minh City' },
  
  // Hanoi (4 locations)
  { lat: 21.0285, lng: 105.8542, name: 'Hoan Kiem Lake, Hanoi', city: 'Hanoi' },
  { lat: 21.0285, lng: 105.8542, name: 'Old Quarter, Hanoi', city: 'Hanoi' },
  { lat: 21.0285, lng: 105.8542, name: 'Temple of Literature, Hanoi', city: 'Hanoi' },
  { lat: 21.0285, lng: 105.8542, name: 'Ho Chi Minh Mausoleum, Hanoi', city: 'Hanoi' },
  
  // Phuket (3 locations)
  { lat: 7.8804, lng: 98.3923, name: 'Patong Beach, Phuket', city: 'Phuket' },
  { lat: 7.8804, lng: 98.3923, name: 'Big Buddha, Phuket', city: 'Phuket' },
  { lat: 7.8804, lng: 98.3923, name: 'Old Town, Phuket', city: 'Phuket' },
  
  // Chiang Mai (3 locations)
  { lat: 18.7883, lng: 98.9853, name: 'Doi Suthep, Chiang Mai', city: 'Chiang Mai' },
  { lat: 18.7883, lng: 98.9853, name: 'Old City, Chiang Mai', city: 'Chiang Mai' },
  { lat: 18.7883, lng: 98.9853, name: 'Sunday Walking Street, Chiang Mai', city: 'Chiang Mai' },
  
  // Penang (3 locations)
  { lat: 5.4164, lng: 100.3327, name: 'George Town, Penang', city: 'Penang' },
  { lat: 5.4164, lng: 100.3327, name: 'Penang Hill, Penang', city: 'Penang' },
  { lat: 5.4164, lng: 100.3327, name: 'Kek Lok Si Temple, Penang', city: 'Penang' },
  
  // Yogyakarta (3 locations)
  { lat: -7.7956, lng: 110.3695, name: 'Borobudur, Yogyakarta', city: 'Yogyakarta' },
  { lat: -7.7956, lng: 110.3695, name: 'Prambanan, Yogyakarta', city: 'Yogyakarta' },
  { lat: -7.7956, lng: 110.3695, name: 'Malioboro Street, Yogyakarta', city: 'Yogyakarta' },
  
  // Bandung (2 locations)
  { lat: -6.9175, lng: 107.6191, name: 'Tangkuban Perahu, Bandung', city: 'Bandung' },
  { lat: -6.9175, lng: 107.6191, name: 'Gedung Sate, Bandung', city: 'Bandung' },
  
  // Surabaya (2 locations)
  { lat: -7.2575, lng: 112.7521, name: 'Heroes Monument, Surabaya', city: 'Surabaya' },
  { lat: -7.2575, lng: 112.7521, name: 'House of Sampoerna, Surabaya', city: 'Surabaya' },
  
  // Perth (3 locations)
  { lat: -31.9505, lng: 115.8605, name: 'Kings Park, Perth', city: 'Perth' },
  { lat: -31.9505, lng: 115.8605, name: 'Fremantle, Perth', city: 'Perth' },
  { lat: -31.9505, lng: 115.8605, name: 'Swan River, Perth', city: 'Perth' },
  
  // Brisbane (3 locations)
  { lat: -27.4698, lng: 153.0251, name: 'South Bank, Brisbane', city: 'Brisbane' },
  { lat: -27.4698, lng: 153.0251, name: 'Lone Pine Koala Sanctuary, Brisbane', city: 'Brisbane' },
  { lat: -27.4698, lng: 153.0251, name: 'Story Bridge, Brisbane', city: 'Brisbane' },
  
  // Adelaide (2 locations)
  { lat: -34.9285, lng: 138.6007, name: 'Adelaide Oval, Adelaide', city: 'Adelaide' },
  { lat: -34.9285, lng: 138.6007, name: 'Barossa Valley, Adelaide', city: 'Adelaide' },
  
  // Wellington (3 locations)
  { lat: -41.2865, lng: 174.7762, name: 'Te Papa Museum, Wellington', city: 'Wellington' },
  { lat: -41.2865, lng: 174.7762, name: 'Cable Car, Wellington', city: 'Wellington' },
  { lat: -41.2865, lng: 174.7762, name: 'Mount Victoria, Wellington', city: 'Wellington' },
  
  // Christchurch (2 locations)
  { lat: -43.5321, lng: 172.6362, name: 'Botanic Gardens, Christchurch', city: 'Christchurch' },
  { lat: -43.5321, lng: 172.6362, name: 'Canterbury Museum, Christchurch', city: 'Christchurch' },
  
  // Montevideo (2 locations)
  { lat: -34.9011, lng: -56.1645, name: 'Old City, Montevideo', city: 'Montevideo' },
  { lat: -34.9011, lng: -56.1645, name: 'Rambla, Montevideo', city: 'Montevideo' },
  
  // Santiago (4 locations)
  { lat: -33.4489, lng: -70.6693, name: 'Plaza de Armas, Santiago', city: 'Santiago' },
  { lat: -33.4489, lng: -70.6693, name: 'San Cristóbal Hill, Santiago', city: 'Santiago' },
  { lat: -33.4489, lng: -70.6693, name: 'La Moneda Palace, Santiago', city: 'Santiago' },
  { lat: -33.4489, lng: -70.6693, name: 'Bellavista, Santiago', city: 'Santiago' },
  
  // Bogotá (3 locations)
  { lat: 4.7110, lng: -74.0721, name: 'La Candelaria, Bogotá', city: 'Bogotá' },
  { lat: 4.7110, lng: -74.0721, name: 'Monserrate, Bogotá', city: 'Bogotá' },
  { lat: 4.7110, lng: -74.0721, name: 'Gold Museum, Bogotá', city: 'Bogotá' },
  
  // Medellín (3 locations)
  { lat: 6.2476, lng: -75.5658, name: 'Botero Plaza, Medellín', city: 'Medellín' },
  { lat: 6.2476, lng: -75.5658, name: 'Comuna 13, Medellín', city: 'Medellín' },
  { lat: 6.2476, lng: -75.5658, name: 'Pueblito Paisa, Medellín', city: 'Medellín' },
  
  // Cartagena (3 locations)
  { lat: 10.3910, lng: -75.4794, name: 'Old City, Cartagena', city: 'Cartagena' },
  { lat: 10.3910, lng: -75.4794, name: 'Castillo San Felipe, Cartagena', city: 'Cartagena' },
  { lat: 10.3910, lng: -75.4794, name: 'Getsemaní, Cartagena', city: 'Cartagena' },
  
  // Quito (3 locations)
  { lat: -0.1807, lng: -78.4678, name: 'Old Town, Quito', city: 'Quito' },
  { lat: -0.1807, lng: -78.4678, name: 'Mitad del Mundo, Quito', city: 'Quito' },
  { lat: -0.1807, lng: -78.4678, name: 'TelefériQo, Quito', city: 'Quito' },
  
  // Guadalajara (3 locations)
  { lat: 20.6597, lng: -103.3496, name: 'Historic Center, Guadalajara', city: 'Guadalajara' },
  { lat: 20.6597, lng: -103.3496, name: 'Tlaquepaque, Guadalajara', city: 'Guadalajara' },
  { lat: 20.6597, lng: -103.3496, name: 'Tequila Region, Guadalajara', city: 'Guadalajara' },
  
  // Cancún (3 locations)
  { lat: 21.1619, lng: -86.8515, name: 'Hotel Zone, Cancún', city: 'Cancún' },
  { lat: 21.1619, lng: -86.8515, name: 'Chichen Itza, Cancún', city: 'Cancún' },
  { lat: 21.1619, lng: -86.8515, name: 'Tulum, Cancún', city: 'Cancún' },
  
  // Tijuana (2 locations)
  { lat: 32.5149, lng: -117.0382, name: 'Avenida Revolución, Tijuana', city: 'Tijuana' },
  { lat: 32.5149, lng: -117.0382, name: 'Zona Río, Tijuana', city: 'Tijuana' },
  
  // Salvador (2 locations)
  { lat: -12.9714, lng: -38.5014, name: 'Pelourinho, Salvador', city: 'Salvador' },
  { lat: -12.9714, lng: -38.5014, name: 'Farol da Barra, Salvador', city: 'Salvador' },
  
  // Brasília (2 locations)
  { lat: -15.7975, lng: -47.8919, name: 'National Congress, Brasília', city: 'Brasília' },
  { lat: -15.7975, lng: -47.8919, name: 'Cathedral of Brasília, Brasília', city: 'Brasília' },
  
  // Curitiba (2 locations)
  { lat: -25.4284, lng: -49.2733, name: 'Jardim Botânico, Curitiba', city: 'Curitiba' },
  { lat: -25.4284, lng: -49.2733, name: 'Ópera de Arame, Curitiba', city: 'Curitiba' },
  
  // Recife (2 locations)
  { lat: -8.0476, lng: -34.8770, name: 'Recife Antigo, Recife', city: 'Recife' },
  { lat: -8.0476, lng: -34.8770, name: 'Boa Viagem Beach, Recife', city: 'Recife' },
  
  // Fortaleza (2 locations)
  { lat: -3.7172, lng: -38.5433, name: 'Praia do Futuro, Fortaleza', city: 'Fortaleza' },
  { lat: -3.7172, lng: -38.5433, name: 'Centro Dragão do Mar, Fortaleza', city: 'Fortaleza' },
  
  // Dakar (3 locations)
  { lat: 14.7167, lng: -17.4677, name: 'Gorée Island, Dakar', city: 'Dakar' },
  { lat: 14.7167, lng: -17.4677, name: 'Monument de la Renaissance, Dakar', city: 'Dakar' },
  { lat: 14.7167, lng: -17.4677, name: 'IFAN Museum, Dakar', city: 'Dakar' },
  
  // Nairobi (3 locations)
  { lat: -1.2921, lng: 36.8219, name: 'Nairobi National Park, Nairobi', city: 'Nairobi' },
  { lat: -1.2921, lng: 36.8219, name: 'Giraffe Centre, Nairobi', city: 'Nairobi' },
  { lat: -1.2921, lng: 36.8219, name: 'Kazuri Beads, Nairobi', city: 'Nairobi' },
  
  // Accra (2 locations)
  { lat: 5.6037, lng: -0.1870, name: 'Independence Square, Accra', city: 'Accra' },
  { lat: 5.6037, lng: -0.1870, name: 'Kwame Nkrumah Mausoleum, Accra', city: 'Accra' },
  
  // Lagos (3 locations)
  { lat: 6.5244, lng: 3.3792, name: 'Lekki Conservation Centre, Lagos', city: 'Lagos' },
  { lat: 6.5244, lng: 3.3792, name: 'National Museum, Lagos', city: 'Lagos' },
  { lat: 6.5244, lng: 3.3792, name: 'Tarkwa Bay, Lagos', city: 'Lagos' },
  
  // Cairo (1 more location)
  { lat: 29.9792, lng: 31.1342, name: 'Khan el-Khalili, Cairo', city: 'Cairo' },
  
  // Alexandria (2 locations)
  { lat: 31.2001, lng: 29.9187, name: 'Bibliotheca Alexandrina, Alexandria', city: 'Alexandria' },
  { lat: 31.2001, lng: 29.9187, name: 'Qaitbay Citadel, Alexandria', city: 'Alexandria' },
  
  // Luxor (2 locations)
  { lat: 25.6872, lng: 32.6396, name: 'Valley of the Kings, Luxor', city: 'Luxor' },
  { lat: 25.6872, lng: 32.6396, name: 'Karnak Temple, Luxor', city: 'Luxor' },
  
  // Aswan (2 locations)
  { lat: 24.0889, lng: 32.8998, name: 'Abu Simbel, Aswan', city: 'Aswan' },
  { lat: 24.0889, lng: 32.8998, name: 'Philae Temple, Aswan', city: 'Aswan' },
  
  // Tunis (2 locations)
  { lat: 36.8065, lng: 10.1815, name: 'Medina, Tunis', city: 'Tunis' },
  { lat: 36.8065, lng: 10.1815, name: 'Bardo Museum, Tunis', city: 'Tunis' },
  
  // Algiers (2 locations)
  { lat: 36.7538, lng: 3.0588, name: 'Casbah, Algiers', city: 'Algiers' },
  { lat: 36.7538, lng: 3.0588, name: 'Notre Dame d\'Afrique, Algiers', city: 'Algiers' },
  
  // Beirut (3 locations)
  { lat: 33.8938, lng: 35.5018, name: 'Downtown Beirut, Beirut', city: 'Beirut' },
  { lat: 33.8938, lng: 35.5018, name: 'National Museum, Beirut', city: 'Beirut' },
  { lat: 33.8938, lng: 35.5018, name: 'Corniche, Beirut', city: 'Beirut' },
  
  // Amman (3 locations)
  { lat: 31.9539, lng: 35.9106, name: 'Citadel, Amman', city: 'Amman' },
  { lat: 31.9539, lng: 35.9106, name: 'Roman Theater, Amman', city: 'Amman' },
  { lat: 31.9539, lng: 35.9106, name: 'Rainbow Street, Amman', city: 'Amman' },
  
  // Petra (2 locations)
  { lat: 30.3285, lng: 35.4444, name: 'The Treasury, Petra', city: 'Petra' },
  { lat: 30.3285, lng: 35.4444, name: 'Monastery, Petra', city: 'Petra' },
  
  // Doha (3 locations)
  { lat: 25.2854, lng: 51.5310, name: 'Souq Waqif, Doha', city: 'Doha' },
  { lat: 25.2854, lng: 51.5310, name: 'Museum of Islamic Art, Doha', city: 'Doha' },
  { lat: 25.2854, lng: 51.5310, name: 'The Pearl, Doha', city: 'Doha' },
  
  // Abu Dhabi (3 locations)
  { lat: 24.4539, lng: 54.3773, name: 'Sheikh Zayed Mosque, Abu Dhabi', city: 'Abu Dhabi' },
  { lat: 24.4539, lng: 54.3773, name: 'Louvre Abu Dhabi, Abu Dhabi', city: 'Abu Dhabi' },
  { lat: 24.4539, lng: 54.3773, name: 'Yas Island, Abu Dhabi', city: 'Abu Dhabi' },
  
  // Muscat (2 locations)
  { lat: 23.5880, lng: 58.3829, name: 'Sultan Qaboos Grand Mosque, Muscat', city: 'Muscat' },
  { lat: 23.5880, lng: 58.3829, name: 'Mutrah Souq, Muscat', city: 'Muscat' },
  
  // Kuwait City (2 locations)
  { lat: 29.3759, lng: 47.9774, name: 'Kuwait Towers, Kuwait City', city: 'Kuwait City' },
  { lat: 29.3759, lng: 47.9774, name: 'Grand Mosque, Kuwait City', city: 'Kuwait City' },
  
  // Riyadh (3 locations)
  { lat: 24.7136, lng: 46.6753, name: 'Kingdom Centre, Riyadh', city: 'Riyadh' },
  { lat: 24.7136, lng: 46.6753, name: 'Al Masmak Fortress, Riyadh', city: 'Riyadh' },
  { lat: 24.7136, lng: 46.6753, name: 'Diriyah, Riyadh', city: 'Riyadh' },
  
  // Jeddah (2 locations)
  { lat: 21.4858, lng: 39.1925, name: 'Al-Balad, Jeddah', city: 'Jeddah' },
  { lat: 21.4858, lng: 39.1925, name: 'King Fahd\'s Fountain, Jeddah', city: 'Jeddah' },
  
  // Lahore (3 locations)
  { lat: 31.5497, lng: 74.3436, name: 'Badshahi Mosque, Lahore', city: 'Lahore' },
  { lat: 31.5497, lng: 74.3436, name: 'Lahore Fort, Lahore', city: 'Lahore' },
  { lat: 31.5497, lng: 74.3436, name: 'Shalimar Gardens, Lahore', city: 'Lahore' },
  
  // Karachi (3 locations)
  { lat: 24.8607, lng: 67.0011, name: 'Clifton Beach, Karachi', city: 'Karachi' },
  { lat: 24.8607, lng: 67.0011, name: 'Mohatta Palace, Karachi', city: 'Karachi' },
  { lat: 24.8607, lng: 67.0011, name: 'National Museum, Karachi', city: 'Karachi' },
  
  // Islamabad (2 locations)
  { lat: 33.6844, lng: 73.0479, name: 'Faisal Mosque, Islamabad', city: 'Islamabad' },
  { lat: 33.6844, lng: 73.0479, name: 'Pakistan Monument, Islamabad', city: 'Islamabad' },
  
  // Dhaka (3 locations)
  { lat: 23.8103, lng: 90.4125, name: 'Lalbagh Fort, Dhaka', city: 'Dhaka' },
  { lat: 23.8103, lng: 90.4125, name: 'Ahsan Manzil, Dhaka', city: 'Dhaka' },
  { lat: 23.8103, lng: 90.4125, name: 'National Parliament, Dhaka', city: 'Dhaka' },
  
  // Colombo (2 locations)
  { lat: 6.9271, lng: 79.8612, name: 'Gangaramaya Temple, Colombo', city: 'Colombo' },
  { lat: 6.9271, lng: 79.8612, name: 'Galle Face Green, Colombo', city: 'Colombo' },
  
  // Kathmandu (3 locations)
  { lat: 27.7172, lng: 85.3240, name: 'Durbar Square, Kathmandu', city: 'Kathmandu' },
  { lat: 27.7172, lng: 85.3240, name: 'Swayambhunath, Kathmandu', city: 'Kathmandu' },
  { lat: 27.7172, lng: 85.3240, name: 'Pashupatinath, Kathmandu', city: 'Kathmandu' },
  
  // Ulaanbaatar (2 locations)
  { lat: 47.8864, lng: 106.9057, name: 'Gandantegchinlen Monastery, Ulaanbaatar', city: 'Ulaanbaatar' },
  { lat: 47.8864, lng: 106.9057, name: 'Sukhbataar Square, Ulaanbaatar', city: 'Ulaanbaatar' },
  
  // Tashkent (2 locations)
  { lat: 41.2995, lng: 69.2401, name: 'Chorsu Bazaar, Tashkent', city: 'Tashkent' },
  { lat: 41.2995, lng: 69.2401, name: 'Independence Square, Tashkent', city: 'Tashkent' },
  
  // Almaty (2 locations)
  { lat: 43.2220, lng: 76.8512, name: 'Medeu, Almaty', city: 'Almaty' },
  { lat: 43.2220, lng: 76.8512, name: 'Kok-Tobe, Almaty', city: 'Almaty' },
  
  // Bishkek (2 locations)
  { lat: 42.8746, lng: 74.5698, name: 'Ala-Too Square, Bishkek', city: 'Bishkek' },
  { lat: 42.8746, lng: 74.5698, name: 'Osh Bazaar, Bishkek', city: 'Bishkek' },
  
  // Tbilisi (3 locations)
  { lat: 41.7151, lng: 44.8271, name: 'Old Town, Tbilisi', city: 'Tbilisi' },
  { lat: 41.7151, lng: 44.8271, name: 'Narikala Fortress, Tbilisi', city: 'Tbilisi' },
  { lat: 41.7151, lng: 44.8271, name: 'Bridge of Peace, Tbilisi', city: 'Tbilisi' },
  
  // Yerevan (2 locations)
  { lat: 40.1792, lng: 44.4991, name: 'Cascade, Yerevan', city: 'Yerevan' },
  { lat: 40.1792, lng: 44.4991, name: 'Republic Square, Yerevan', city: 'Yerevan' },
  
  // Baku (3 locations)
  { lat: 40.4093, lng: 49.8671, name: 'Flame Towers, Baku', city: 'Baku' },
  { lat: 40.4093, lng: 49.8671, name: 'Old City, Baku', city: 'Baku' },
  { lat: 40.4093, lng: 49.8671, name: 'Heydar Aliyev Center, Baku', city: 'Baku' },
  
  // Dushanbe (2 locations)
  { lat: 38.5598, lng: 68.7870, name: 'Rudaki Park, Dushanbe', city: 'Dushanbe' },
  { lat: 38.5598, lng: 68.7870, name: 'National Museum, Dushanbe', city: 'Dushanbe' },
  
  // Ashgabat (2 locations)
  { lat: 37.9601, lng: 58.3261, name: 'Independence Monument, Ashgabat', city: 'Ashgabat' },
  { lat: 37.9601, lng: 58.3261, name: 'Turkmenbashi Ruhy Mosque, Ashgabat', city: 'Ashgabat' }
];

// Profile data arrays
// NOTE: These are intentionally large so "populate" rarely generates duplicate names.
const firstNames = [
  'Emma','Michael','Sarah','David','Lisa','James','Maria','Robert','Jennifer','Thomas',
  'Emily','Christopher','Jessica','Daniel','Amanda','Matthew','Lauren','Andrew','Nicole','Kevin',
  'Rachel','Ryan','Samantha','Justin','Stephanie','Brandon','Michelle','Jordan','Brittany','Tyler',
  'Ashley','Zachary','Megan','Austin','Kayla','Nathan','Hannah','Cameron','Olivia','Ethan',
  'Sofia','Connor','Isabella','Jacob','Chloe','Dylan','Grace','Lucas','Lily','Ava',
  'Noah','Liam','Mia','Evelyn','Harper','Ella','Aria','Scarlett','Layla','Nora',
  'Amelia','Zoe','Riley','Stella','Hazel','Aurora','Violet','Penelope','Paisley','Naomi',
  'Elijah','William','Benjamin','Henry','Theodore','Jack','Leo','Oliver','Alexander','Sebastian',
  'Eleanor','Charlotte','Abigail','Madison','Victoria','Camila','Luna','Gianna','Aaliyah','Savannah'
];

const lastNames = [
  'Johnson','Chen','Williams','Brown','Anderson','Wilson','Garcia','Taylor','Martinez','Lee',
  'Davis','Moore','Jackson','White','Harris','Clark','Lewis','Walker','Hall','Allen',
  'Young','King','Wright','Lopez','Hill','Scott','Green','Adams','Baker','Nelson',
  'Carter','Mitchell','Perez','Roberts','Turner','Phillips','Campbell','Parker','Evans','Edwards',
  'Collins','Stewart','Sanchez','Morris','Rogers','Reed','Cook','Morgan','Bell','Bailey',
  'Rivera','Cooper','Richardson','Cox','Howard','Ward','Torres','Peterson','Gray','Ramirez',
  'James','Watson','Brooks','Kelly','Sanders','Price','Bennett','Wood','Barnes','Ross',
  'Henderson','Coleman','Jenkins','Perry','Powell','Long','Patterson','Hughes','Flores','Washington'
];

const toUsername = (fullName) =>
  fullName
    .toLowerCase()
    .replace(/[^a-z\s]/g, '')
    .trim()
    .replace(/\s+/g, '_');

const buildNamePool = () => {
  const names = [];
  for (const f of firstNames) {
    for (const l of lastNames) {
      names.push(`${f} ${l}`);
    }
  }
  return names;
};

export const randomNames = buildNamePool();
export const randomUsernames = randomNames.map(toUsername);

export const allTags = [
  '#photography', '#travel', '#coffee', '#fitness', '#music', '#coding', '#gaming', '#tech', '#startup', '#design',
  '#yoga', '#meditation', '#wellness', '#nature', '#books', '#foodie', '#cooking', '#restaurant', '#wine', '#chef',
  '#art', '#painting', '#drawing', '#gallery', '#creative', '#sports', '#basketball', '#football', '#running', '#cycling',
  '#business', '#entrepreneur', '#networking', '#leadership', '#strategy', '#fashion', '#style', '#designer', '#shopping', '#trends',
  '#guitar', '#piano', '#concerts', '#bands', '#science', '#research', '#innovation', '#technology', '#discovery', '#writing',
  '#blogging', '#poetry', '#literature', '#journalism', '#gardening', '#plants', '#flowers', '#organic', '#green', '#dancing',
  '#ballet', '#hiphop', '#salsa', '#performance', '#movies', '#cinema', '#directing', '#acting', '#film', '#animals',
  '#pets', '#dogs', '#cats', '#wildlife', '#architecture', '#buildings', '#urban', '#construction', '#education', '#teaching',
  '#learning', '#students', '#knowledge', '#health', '#medicine', '#nutrition', '#cars', '#automotive', '#racing', '#mechanics',
  '#driving', '#esports', '#streaming', '#console', '#pc', '#investing', '#developer', '#hiking', '#party', '#tennis'
];

export const allGroups = [
  'Photography Club', 'Travel Enthusiasts', 'Coffee Lovers', 'Dev Community', 'Startup Hub', 'Tech Innovators',
  'Yoga Masters', 'Wellness Circle', 'Mindful Living', 'Food Critics', 'Chef Network', 'Wine Society',
  'Art Collective', 'Creative Minds', 'Gallery Owners', 'Sports Team', 'Fitness Group', 'Athletes United',
  'Business Network', 'Entrepreneurs', 'Leadership Forum', 'Fashion Week', 'Style Council', 'Designers Guild',
  'Music Scene', 'Band Members', 'Concert Goers', 'Science Lab', 'Research Team', 'Innovation Hub',
  'Writers Guild', 'Bloggers Network', 'Poetry Circle', 'Gardeners Club', 'Plant Parents', 'Green Thumbs',
  'Dance Academy', 'Performance Arts', 'Movement Studio', 'Film Makers', 'Cinema Society', 'Movie Buffs',
  'Pet Lovers', 'Animal Rescue', 'Wildlife Conservation', 'Architects Guild', 'Design Studio', 'Urban Planning',
  'Teachers Network', 'Education Hub', 'Learning Circle', 'Health Professionals', 'Medical Network', 'Wellness Center',
  'Car Enthusiasts', 'Racing Team', 'Auto Club', 'Gaming Community', 'ESports League', 'Streamers Network'
];

export const socialPlatforms = ['facebook.com', 'instagram.com', 'twitter.com', 'linkedin.com', 't.me', 'youtube.com', 'tiktok.com', 'snapchat.com'];

export const personalDataFields = [
  { label: 'Occupation', values: ['Software Engineer', 'Designer', 'Teacher', 'Doctor', 'Artist', 'Writer', 'Chef', 'Photographer', 'Musician', 'Consultant', 'Nurse', 'Engineer', 'Lawyer', 'Accountant', 'Marketing Manager', 'Sales Representative', 'Project Manager', 'Data Scientist', 'Product Manager', 'Business Analyst', 'HR Manager', 'Financial Advisor', 'Architect', 'Real Estate Agent', 'Entrepreneur', 'Student', 'Researcher', 'Scientist', 'Veterinarian', 'Dentist', 'Therapist', 'Coach', 'Translator', 'Journalist', 'Editor', 'Graphic Designer', 'Web Developer', 'Mechanic', 'Electrician', 'Plumber', 'Construction Worker', 'Pilot', 'Flight Attendant', 'Waiter', 'Bartender', 'Retail Worker', 'Customer Service', 'Other...'] },
  { label: 'Education', values: ['University Degree', 'Masters Degree', 'PhD', 'High School', 'College'] },
  { label: 'Languages', values: ['English, Spanish', 'English, French', 'English, German', 'English, Chinese', 'English, Japanese', 'English, Italian', 'Multilingual'] },
  { label: 'Interests', values: ['Reading', 'Traveling', 'Cooking', 'Sports', 'Music', 'Art', 'Photography', 'Gaming'] },
  { label: 'Relationship', values: ['Single', 'In a relationship', 'Married', 'Divorced', 'Separated', 'Widowed', 'Open relationship', 'Do not want relationships', "It's complicated"] },
  { label: 'Workplace', values: ['Remote', 'Hybrid', 'Office', 'Freelance'] }
];

export const birthYears = Array.from({length: 40}, (_, i) => 1975 + i);

// Function to generate profile data for a location
export const generateProfileData = (location) => {
  const seed = hashLocation(location.name);
  const random = seededRandom(seed);
  
  const randomIndex = Math.floor(random() * randomNames.length);
  const name = randomNames[randomIndex];
  // Ensure deterministic uniqueness even if two locations collide on randomIndex.
  const baseUsername = randomUsernames[randomIndex];
  const suffix = (seed % 100000).toString().padStart(5, '0');
  const username = `${baseUsername}_${suffix}`;
  const city = location.city;
  const birthYear = birthYears[Math.floor(random() * birthYears.length)];
  const age = 2025 - birthYear;
  const day = Math.floor(random() * 28) + 1;
  const month = Math.floor(random() * 12) + 1;
  const birthDate = `${String(day).padStart(2, '0')}.${String(month).padStart(2, '0')}.${birthYear}`;
  
  // Random rating (0-5 stars) and survey count
  const ratingStars = Math.floor(random() * 6); // 0-5 stars
  const surveyCount = Math.floor(random() * 1000) + 1; // 1-1000 surveys
  
  // Random number of tags (1-10)
  const numTags = Math.floor(random() * 10) + 1;
  const shuffledTags = [...allTags].sort(() => random() - 0.5);
  const tags = shuffledTags.slice(0, numTags);
  
  // Random number of groups (1-5)
  const numGroups = Math.floor(random() * 5) + 1;
  const shuffledGroups = [...allGroups].sort(() => random() - 0.5);
  const groups = shuffledGroups.slice(0, numGroups);
  
  // Random number of social links (1-5)
  const numSocials = Math.floor(random() * 5) + 1;
  const shuffledPlatforms = [...socialPlatforms].sort(() => random() - 0.5);
  const socialLinks = shuffledPlatforms.slice(0, numSocials).map(platform => `${platform}/${username}`);
  
  // Random additional personal data (1-3 fields)
  const numPersonalFields = Math.floor(random() * 3) + 1;
  const shuffledPersonalFields = [...personalDataFields].sort(() => random() - 0.5);
  const additionalPersonalData = shuffledPersonalFields.slice(0, numPersonalFields).map(field => ({
    label: field.label,
    value: field.values[Math.floor(random() * field.values.length)]
  }));
  
  const tagsCount = (random() * 2 + 0.5).toFixed(6);
  
  return {
    name,
    username,
    rating: ratingStars,
    surveyCount,
    tags: parseFloat(tagsCount),
    tagsList: tags,
    groups,
    socialLinks,
    birthDate,
    age,
    city,
    location: location.name,
    avatar: getAvatarUrl(username),
    additionalPersonalData
  };
};

